import { onDestroy, onTrack, onView } from "@/modules/_";
import { Resize } from "@lib/subs";
import gsap, { SplitText } from "@lib/gsap";
import { colors } from "@/utils/colors";

const PARALLAX_FACTOR = 0.4;
const PARALLAX_VARIANCE = 0.3;
const PARALLAX_DIRECTION = 1;

const FLASH_BASE_COLOR = colors.oat[100];
const FLASH_COLOR_A = colors.oat[950];
const FLASH_COLOR_B = colors.lime[200];
const FLASH_STAGGER_S = 0.04;
const FLASH_STEP_S = 0.4;
const FLASH_VIEWPORT_THRESHOLD = 0.1;

export default function (element: HTMLElement, dataset: DOMStringMap) {
  const paragraphs = Array.from(
    element.querySelectorAll<HTMLElement>('[intro-plx="p"]'),
  );
  const splits = new Map<HTMLElement, SplitText>();

  function buildSplits() {
    paragraphs.forEach((p) => {
      splits.get(p)?.revert();
      const s = new SplitText(p, {
        type: "lines, words",
        linesClass: "split-line",
        wordsClass: "split-word",
      });
      splits.set(p, s);
      gsap.set(s.words, { color: FLASH_BASE_COLOR });
    });
    retryPendingFlashes();
  }

  // Falls back to immediate split if Font Loading API isn't available.
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => {
      buildSplits();
    });
  } else {
    buildSplits();
  }

  // Mobile browsers fire resize on every address-bar collapse/expand (height
  // only), which would thrash splits — skip when width is unchanged.
  let lastBuildWidth = window.innerWidth;
  const offResizeSplits = Resize.add((data: { width: number }) => {
    if (data.width === lastBuildWidth) return;
    lastBuildWidth = data.width;
    buildSplits();
  });

  const items = Array.from(
    element.querySelectorAll<HTMLElement>('[intro-plx="item"]'),
  );
  const flashed = new Set<HTMLElement>();
  const pendingFlash = new Set<HTMLElement>();

  function playFlash(words: HTMLElement[]) {
    const tl = gsap.timeline();
    words.forEach((word, i) => {
      const t = i * FLASH_STAGGER_S;
      tl.to(
        word,
        { color: FLASH_COLOR_A, duration: FLASH_STEP_S, ease: "expo.out" },
        t,
      );
      tl.to(
        word,
        { color: FLASH_COLOR_B, duration: FLASH_STEP_S, ease: "expo.out" },
        t + FLASH_STEP_S,
      );
      tl.to(
        word,
        { color: FLASH_BASE_COLOR, duration: FLASH_STEP_S, ease: "expo.out" },
        t + FLASH_STEP_S * 2,
      );
    });
  }

  function tryFlash(item: HTMLElement) {
    if (flashed.has(item)) return;
    const p = item.querySelector<HTMLElement>('[intro-plx="p"]');
    if (!p) return;
    const split = splits.get(p);
    if (!split) {
      pendingFlash.add(item);
      return;
    }
    flashed.add(item);
    pendingFlash.delete(item);
    playFlash(split.words as HTMLElement[]);
  }

  function retryPendingFlashes() {
    if (pendingFlash.size === 0) return;
    Array.from(pendingFlash).forEach(tryFlash);
  }

  items.forEach((item) => {
    onView(item, {
      threshold: FLASH_VIEWPORT_THRESHOLD,
      autoStart: true,
      once: true,
      callback: ({ isIn }) => {
        if (isIn) tryFlash(item);
      },
    });
  });

  const clouds = Array.from(
    element.querySelectorAll<HTMLElement>('[intro-plx="cloud"]'),
  );
  if (clouds.length === 0) {
    onDestroy(() => {
      offResizeSplits();
      splits.forEach((s) => s.revert());
      splits.clear();
    });
    return;
  }

  const meta = new Map<
    HTMLElement,
    { factor: number; range: number; setY: (v: number) => void }
  >();

  clouds.forEach((cloud) => {
    cloud.style.willChange = "transform";
    const jitter = 1 + (Math.random() * 2 - 1) * PARALLAX_VARIANCE;
    const factor = PARALLAX_FACTOR * jitter;
    meta.set(cloud, {
      factor,
      range: cloud.getBoundingClientRect().height * factor,
      setY: gsap.quickSetter(cloud, "y", "px") as (v: number) => void,
    });
  });

  function remeasure() {
    clouds.forEach((cloud) => {
      const m = meta.get(cloud);
      if (m) m.range = cloud.getBoundingClientRect().height * m.factor;
    });
  }
  const offResize = Resize.add(remeasure);

  clouds.forEach((cloud) => {
    onTrack(cloud, {
      bounds: [0, 1],
      top: "bottom",
      bottom: "top",
      callback: (t) => {
        const m = meta.get(cloud);
        if (!m) return;
        m.setY((0.5 - t) * m.range * PARALLAX_DIRECTION);
      },
    });
  });

  onDestroy(() => {
    offResize();
    offResizeSplits();
    splits.forEach((s) => s.revert());
    splits.clear();
    clouds.forEach((cloud) => {
      gsap.set(cloud, { clearProps: "y,transform" });
      cloud.style.willChange = "";
    });
  });
}
